import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import DoctorCard from "../../components/doctors/DoctorCard";
import BulkTerritoryBar from "./BulkTerritoryBar";
import TerritorySuggestionPanel from "./TerritorySuggestionPanel";
import StickyHeading from "../../components/common/StickyHeading";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { isPendingMasterChange, sentForApprovalMessage } from "../../lib/masterChange";
import {
  Plus,
  Search,
  SlidersHorizontal,
  Stethoscope,
  X,
  Sparkles,
  Archive,
  RotateCcw,
} from "lucide-react";

const ALL = "__all__";

export default function DoctorListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(ALL);
  const [spec, setSpec] = useState(ALL);
  // Seeded from `?assigned_to=`, so the manager dashboard's "My Doctors" tile
  // opens the same set it counted. Both sides go through the backend's
  // `owned_doctor_query`, which covers BOTH ownership slots — a drill-down that
  // returned a different population from the tile above it is the defect #37
  // exists to remove, and it would be no better on this screen.
  const [searchParams] = useSearchParams();
  const [assignedTo, setAssignedTo] = useState(searchParams.get("assigned_to") || ALL);
  const [sos, setSos] = useState([]);

  // Specializations come from the backend, never a frontend copy. A hardcoded
  // list here is what #78 was, and `/doctors/meta/options` serves the SAME
  // `SPECIALIZATIONS` that `SpecializationType` validates against (they are one
  // object — see routes_doctors). Fetching it is what makes the dropdown and
  // the validator incapable of disagreeing.
  const [specializations, setSpecializations] = useState([]);
  useEffect(() => {
    api.get("/doctors/meta/options")
      .then(({ data }) => setSpecializations(data.specializations || []))
      .catch(() => {}); // the picker shows nothing rather than a stale guess
  }, []);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [acting, setActing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const canAdd =
    user?.role === "director" || user?.role === "admin" || user?.role === "assistant_manager" || user?.role === "admin_manager";
  // Archive / restore mirrors the backend gate on DELETE /doctors/{id}.
  const canArchive = user?.role === "director" || user?.role === "admin";
  const showAssigneeFilter = user?.role !== "sales_officer";
  // Bulk territory resolution is master-data administration — same roles that
  // hold the territory master itself (mirrors the backend gate).
  const canBulkEditTerritory =
    user?.role === "director" || user?.role === "admin" || user?.role === "admin_manager";

  async function archiveDoctor() {
    if (!archiveTarget) return;
    setActing(true);
    try {
      const { data } = await api.delete(`/doctors/${archiveTarget.id}`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Doctor archived");
      setArchiveTarget(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  async function restoreDoctor(doctor) {
    try {
      await api.put(`/doctors/${doctor.id}`, { is_active: true });
      toast.success("Doctor restored");
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  // Fetch SOs for the assigned-to filter (director/admin/AM only).
  useEffect(() => {
    if (!showAssigneeFilter) return;
    (async () => {
      try {
        const { data } = await api.get("/users");
        // Field staff = Sales Officers and Sales Managers. Managers can own
        // doctors (solely or jointly), so they belong in the assignee filter.
        const filtered = data.filter(
          (u) => ["sales_officer", "assistant_manager"].includes(u.role) && u.is_active
        );
        // A manager sees their own team plus themselves; admin/director see all.
        const forManager = user?.role === "assistant_manager" || user?.role === "admin_manager";
        setSos(
          forManager
            ? filtered.filter((u) => u.manager_id === user.id || u.id === user.id)
            : filtered
        );
      } catch (_) {
        // non-fatal
      }
    })();
  }, [showAssigneeFilter, user]);

  useEffect(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (category !== ALL) params.category = category;
    if (spec !== ALL) params.specialization = spec;
    if (assignedTo !== ALL) params.assigned_to = assignedTo;
    if (showArchived) params.include_inactive = true;

    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get("/doctors", { params })
      .then(({ data }) => {
        if (!cancelled) setDoctors(data);
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [search, category, spec, assignedTo, showArchived, reloadKey]);

  const hasFilters =
    !!search.trim() || category !== ALL || spec !== ALL || assignedTo !== ALL;

  const clearFilters = () => {
    setSearch("");
    setCategory(ALL);
    setSpec(ALL);
    setAssignedTo(ALL);
  };

  const summary = useMemo(() => {
    const byCat = { A: 0, B: 0, C: 0 };
    doctors.forEach((d) => {
      if (byCat[d.category] != null) byCat[d.category] += 1;
    });
    return byCat;
  }, [doctors]);

  return (
    <div className="space-y-6" data-testid="doctor-list-page">
      <StickyHeading
        icon={Sparkles}
        label="Doctor Master"
        title="Doctors"
        subtitle={
          user?.role === "sales_officer"
            ? "Doctors assigned to you. Tap any card to open the full profile."
            : "Browse, filter, and manage every doctor in your territory."
        }
        testId="doctor-list-heading"
        actions={
          canAdd && (
            <Button
              onClick={() => navigate("/doctors/new")}
              className="bg-callisto-navy hover:bg-callisto-navy/90 text-white h-11 px-5"
              data-testid="doctor-add-button"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Doctor
            </Button>
          )
        }
      />

      {/* D2: the ~95 doctors with no unit, each with a SUGGESTED zone and the
          evidence for it. Sits above the flagged-value bar because they are
          different populations — no unit at all, versus a legacy string that
          needs review — and conflating them is how a bulk tool applies the
          wrong fix to the wrong rows. */}
      {canBulkEditTerritory && <TerritorySuggestionPanel onApplied={() => setReloadKey((k) => k + 1)} />}

      <BulkTerritoryBar
        doctors={doctors}
        canBulkEdit={canBulkEditTerritory}
        onDone={() => setReloadKey((k) => k + 1)}
      />

      {/* Filters */}
      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-callisto-muted" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, clinic, or territory"
              className="pl-9 h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="doctor-search-input"
            />
          </div>

          <div className="grid grid-cols-2 md:flex md:items-center gap-2">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                className="h-11 md:w-36 focus:ring-callisto-gold"
                data-testid="doctor-filter-category"
              >
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                <SelectItem value="A">A — High value</SelectItem>
                <SelectItem value="B">B — Medium value</SelectItem>
                <SelectItem value="C">C — Standard</SelectItem>
              </SelectContent>
            </Select>

            <Select value={spec} onValueChange={setSpec}>
              <SelectTrigger
                className="h-11 md:w-48 focus:ring-callisto-gold"
                data-testid="doctor-filter-specialization"
              >
                <SelectValue placeholder="Specialization" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All specializations</SelectItem>
                {specializations.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {showAssigneeFilter && (
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger
                  className="h-11 md:w-44 col-span-2 focus:ring-callisto-gold"
                  data-testid="doctor-filter-assignee"
                >
                  <SelectValue placeholder="Assigned to" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All officers &amp; managers</SelectItem>
                  {sos.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {hasFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="h-11 text-callisto-muted hover:text-callisto-navy"
                data-testid="doctor-filters-clear"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {canArchive && (
            <label className="mt-3 inline-flex items-center gap-2 text-xs text-callisto-body cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-4 w-4 rounded border-callisto accent-callisto-navy"
                data-testid="doctor-show-archived"
              />
              Show archived doctors
            </label>
          )}
        </div>

        {!loading && doctors.length > 0 && (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-callisto-muted">
            <SlidersHorizontal size={12} />
            <span>
              <span className="font-bold text-callisto-navy">{doctors.length}</span> doctor
              {doctors.length === 1 ? "" : "s"} · A {summary.A} · B {summary.B} · C {summary.C}
            </span>
          </div>
        )}
      </div>

      {/* Results */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-44 w-full rounded-xl" />
          ))}
        </div>
      ) : doctors.length === 0 ? (
        <EmptyState canAdd={canAdd} hasFilters={hasFilters} clearFilters={clearFilters} />
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
          data-testid="doctor-list-grid"
        >
          {doctors.map((d) => (
            <DoctorCard
              key={d.id}
              doctor={d}
              actions={
                !canArchive ? null : d.is_active === false ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => restoreDoctor(d)}
                    className="h-8 flex-1 border-callisto text-callisto-navy text-[11px] font-semibold"
                    data-testid={`doctor-restore-${d.id}`}
                  >
                    <RotateCcw size={11} className="mr-1" /> Restore
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setArchiveTarget(d)}
                    className="h-8 flex-1 border-red-200 text-red-700 hover:bg-red-50 text-[11px] font-semibold"
                    data-testid={`doctor-archive-${d.id}`}
                  >
                    <Archive size={11} className="mr-1" /> Archive
                  </Button>
                )
              }
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this doctor?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-callisto-navy">{archiveTarget?.name}</span> will be
              hidden from the doctor list and from assignment pickers. The record and all visit
              history are retained — tick “Show archived doctors” to find and restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={acting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); archiveDoctor(); }}
              disabled={acting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="doctor-archive-confirm"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ canAdd, hasFilters, clearFilters }) {
  return (
    <div
      className="bg-white border border-callisto rounded-xl p-10 md:p-14 flex flex-col items-center text-center"
      data-testid="doctor-list-empty"
    >
      <div className="h-16 w-16 rounded-2xl bg-callisto-navy/5 flex items-center justify-center mb-4">
        <Stethoscope className="h-7 w-7 text-callisto-navy" strokeWidth={1.5} />
      </div>
      <h3 className="text-lg font-bold text-callisto-navy">No doctors found</h3>
      <p className="mt-1 text-sm text-callisto-muted max-w-md">
        {hasFilters
          ? "Try clearing filters to see all doctors."
          : "Doctors will appear here once they're added to the master."}
      </p>
      <div className="mt-4 flex items-center gap-3">
        {hasFilters && (
          <Button variant="outline" onClick={clearFilters} className="h-10 border-callisto">
            Clear filters
          </Button>
        )}
        {canAdd && (
          <Link
            to="/doctors/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-callisto-navy text-white px-4 py-2.5 text-sm font-semibold hover:bg-callisto-navy/90"
          >
            <Plus className="h-4 w-4" />
            Add Doctor
          </Link>
        )}
      </div>
    </div>
  );
}
